import {
  useGetShareFiltersMutation,
  useGetTourSearchLinksResultsQuery,
} from '@carboncopyco/data';
import { Separator, TextLargeParagraph } from '@carboncopyco/ui';
import { capitalize } from '@carboncopyco/utils';
import { useUpdateEffect } from 'ahooks';
import { useEffect } from 'react';
import { BadgeWithTooltip } from '../../../BadgeWithTooltip';
import { SortDropdown } from '../../../UrlStateControls';
import { useUrlState } from '../../../utils';
import { MultiStringFilter } from '../../Controls/MultiStringFilter';
import { StatusFilter } from '../../Controls/StatusFilter';
import { CollectionCard } from '../../InfoPageCards';
import { TabLayout } from '../../TabLayout';
import type { UrlState } from '../../types';
import { TourPageActionButtons } from '../TourPageActionButtons';
import { TourPageBreadcrumbs } from '../TourPageBreadcrumbs';
import { useTourPageProps } from '../TourPageContext';
import { TourPageTitle } from '../TourPageTitle';
import { LinksTable } from './LinksTable';

export const LinksTab = () => {
  const { organizationId, workspaceId, tourId, readOnly } = useTourPageProps();
  const { data: linksResponse } = useGetTourSearchLinksResultsQuery({
    organizationId,
    id: String(tourId),
    workspaces: [workspaceId],
  });
  const { items: links, metadata } = linksResponse || {};

  const [urlState, setUrlsState] = useUrlState<UrlState>({});
  const { query } = urlState;

  const [getShareFilters, { data: filters }] = useGetShareFiltersMutation();
  useEffect(() => {
    getShareFilters({
      workspaces: [workspaceId],
      organizationId: Number(organizationId),
      entityType: 'tour',
      entityId: Number(tourId),
      query,
    });
  }, [getShareFilters, workspaceId, tourId, organizationId, query]);

  useUpdateEffect(() => {
    setUrlsState({
      creators: undefined,
      active: undefined,
    });
  }, [tourId, organizationId, query]);

  const creatorOptions =
    filters?.creators?.map((creator) => ({
      id: creator.userId,
      label: capitalize(creator.userName),
    })) || [];

  return (
    <TabLayout
      title={<TourPageTitle />}
      entity="tour"
      breadcrumbs={<TourPageBreadcrumbs />}
      actionButtons={<TourPageActionButtons />}
      secondaryActionButtons={
        readOnly ? (
          <div className="flex items-center gap-1">
            <BadgeWithTooltip
              tooltipText="This asset is shared from another workspace. You can view and present it."
              badgeProps={{
                label: 'Read only',
                variant: 'primary',
              }}
            />
          </div>
        ) : null
      }
    >
      <div className="pb-10">
        <CollectionCard
          withSearch
          inputPlaceholder="Search by link name"
          radius
          header={
            <div className="flex gap-2 py-6">
              <TextLargeParagraph
                color="text-ds-natural-700"
                label="Tour shares"
              />
              {!!links?.length && (
                <TextLargeParagraph
                  color="text-ds-natural-300"
                  label={`(${metadata?.totalItems})`}
                />
              )}
            </div>
          }
        >
          <>
            <div className="mt-1" />
            <div className="flex p-4 mt-1 gap-3">
              <SortDropdown />
              <div>
                <Separator />
              </div>
              <MultiStringFilter
                capsuleWidth={192}
                placeholder="Created by"
                options={creatorOptions}
                type="creators"
              />
              <StatusFilter />
            </div>

            <LinksTable />
          </>
        </CollectionCard>
      </div>
    </TabLayout>
  );
};
